/**
 * Delete Menu Dialog Component
 * Confirmation dialog for deleting a menu
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatDate, fromISODate } from '@/lib/utils/dateHelpers';
import type { Menu } from '@/types/api';

interface DeleteMenuDialogProps {
  menu: Menu | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

/**
 * DeleteMenuDialog - Confirmation for menu deletion
 */
export function DeleteMenuDialog({
  menu,
  open,
  onOpenChange,
  onConfirm,
  isDeleting = false,
}: DeleteMenuDialogProps) {
  if (!menu) return null;

  const dateDisplay = formatDate(fromISODate(menu.date), 'EEEE, MMMM d, yyyy');

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Menu</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>Are you sure you want to delete this menu?</p>
            <div className="bg-muted p-3 rounded-md mt-2">
              <p className="font-medium">{menu.title}</p>
              <p className="text-sm text-muted-foreground">{dateDisplay}</p>
            </div>
            <p className="text-sm text-destructive mt-2">
              This action cannot be undone.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
